import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EstadoError, EstadoTodoBien, ListaEsqueleto } from '../../components/ui/Estados';
import { Input, Select } from '../../components/ui/Input';
import { Tabla, type Columna } from '../../components/ui/Tabla';
import { BarraFiltros, type ChipFiltro } from '../../components/layout/BarraFiltros';
import { CabeceraPagina } from '../../components/layout/CabeceraPagina';
import {
  repositorioEnergiaUmbrales,
  type AlarmaEnergia,
} from '../../../data/repositories/RepositorioEnergiaUmbrales';
import {
  describirVariable,
  formatearValorVariable,
} from '../../../domain/value-objects/VariableMedidor';
import { useTiempoReal } from '../../hooks/useTiempoReal';
import { formatearFechaHora } from '../../utils/formato';
import estilos from './Umbrales.module.css';
import { Aviso } from '../../components/ui/Aviso';

/**
 * Filtro de propagación.
 *
 * Una alarma de energía debería replicarse en el sistema central de alarmas,
 * que es el que dispara las notificaciones. Si no llegó a hacerlo queda
 * «suelta»: se ve aquí pero nadie recibió aviso. Poder listarlas es la forma
 * de detectar que algo falló en esa cadena.
 */
type FiltroPropagacion = 'todas' | 'propagadas' | 'sueltas';

export function AlarmasEnergia() {
  const clienteConsultas = useQueryClient();

  const [busqueda, setBusqueda] = useState('');
  const [estado, setEstado] = useState<'todas' | 'activa' | 'resuelta'>('todas');
  const [propagacion, setPropagacion] = useState<FiltroPropagacion>('todas');
  const [medidorFiltro, setMedidorFiltro] = useState('');

  const consulta = useQuery({
    queryKey: ['alarmas-energia'],
    queryFn: () => repositorioEnergiaUmbrales.listarAlarmas(),
  });

  const refrescar = () => {
    void clienteConsultas.invalidateQueries({ queryKey: ['alarmas-energia'] });
  };

  useTiempoReal({ tabla: 'smart_meter_alarms', evento: '*' }, refrescar);

  const alarmas = useMemo(() => consulta.data ?? [], [consulta.data]);

  const filtradas = useMemo(
    () =>
      alarmas.filter((a) => {
        if (estado !== 'todas' && a.estado.valor !== estado) return false;
        if (propagacion === 'propagadas' && !a.alarmaCentralId) return false;
        if (propagacion === 'sueltas' && a.alarmaCentralId) return false;
        if (medidorFiltro && a.medidorNombre !== medidorFiltro) return false;

        const t = busqueda.trim().toLowerCase();
        if (!t) return true;

        return [a.medidorNombre, describirVariable(a.variable).etiqueta, a.mensaje].some((campo) =>
          campo?.toLowerCase().includes(t),
        );
      }),
    [alarmas, busqueda, estado, propagacion, medidorFiltro],
  );

  /** Medidores presentes en el listado, para el desplegable. */
  const medidores = useMemo(
    () => [...new Set(alarmas.map((a) => a.medidorNombre).filter(Boolean))].sort(),
    [alarmas],
  );

  const sueltas = useMemo(() => alarmas.filter((a) => !a.alarmaCentralId).length, [alarmas]);

  const chips: ChipFiltro[] = [];
  if (estado !== 'todas') {
    chips.push({
      clave: 'estado',
      etiqueta: estado === 'activa' ? 'Solo activas' : 'Solo resueltas',
      quitar: () => setEstado('todas'),
    });
  }
  if (propagacion !== 'todas') {
    chips.push({
      clave: 'propagacion',
      etiqueta: propagacion === 'propagadas' ? 'Solo propagadas' : 'Solo sin propagar',
      quitar: () => setPropagacion('todas'),
    });
  }
  if (busqueda.trim()) {
    chips.push({
      clave: 'busqueda',
      etiqueta: `Búsqueda: «${busqueda.trim()}»`,
      quitar: () => setBusqueda(''),
    });
  }

  const columnas: Columna<AlarmaEnergia>[] = [
    {
      clave: 'medidor',
      encabezado: 'Medidor',
      movil: 'titulo',
      render: (a) => <span className={estilos.sensor}>{a.medidorNombre}</span>,
    },
    {
      clave: 'variable',
      encabezado: 'Magnitud',
      movil: 'subtitulo',
      render: (a) => describirVariable(a.variable).etiqueta,
    },
    {
      clave: 'valor',
      encabezado: 'Valor',
      render: (a) => (
        <span className={estilos.rango}>{formatearValorVariable(a.variable, a.valorDisparo)}</span>
      ),
    },
    {
      clave: 'violacion',
      encabezado: 'Motivo',
      render: (a) =>
        a.tipoViolacion === 'min' ? (
          <Badge color="var(--status-info)">Por debajo del mínimo</Badge>
        ) : a.tipoViolacion === 'max' ? (
          <Badge color="var(--status-warning)">Por encima del máximo</Badge>
        ) : (
          '—'
        ),
    },
    {
      clave: 'criticidad',
      encabezado: 'Gravedad',
      render: (a) => <Badge color={a.criticidad.color}>{a.criticidad.etiqueta}</Badge>,
    },
    {
      clave: 'estado',
      encabezado: 'Estado',
      render: (a) => <Badge color={a.estado.color}>{a.estado.etiqueta}</Badge>,
    },
    {
      clave: 'aviso',
      encabezado: 'Aviso enviado',
      render: (a) =>
        a.alarmaCentralId ? (
          <Badge color="var(--status-success)">Sí</Badge>
        ) : (
          <Badge
            color="var(--status-danger)"
            title="No se replicó en el sistema central de alarmas"
          >
            No
          </Badge>
        ),
    },
    {
      clave: 'inicio',
      encabezado: 'Inicio',
      render: (a) => formatearFechaHora(a.fechaInicio),
    },
    {
      clave: 'fin',
      encabezado: 'Resolución',
      render: (a) => formatearFechaHora(a.fechaResolucion),
    },
  ];

  return (
    <div className={estilos.pagina}>
      <CabeceraPagina
        titulo="Alarmas de energía"
        descripcion="Alarmas generadas por los umbrales de los medidores."
        acciones={
          <Button
            variante="secundario"
            icono="refrescar"
            compactoEnMovil
            onClick={refrescar}
            cargando={consulta.isFetching}
          >
            Actualizar
          </Button>
        }
      />

      {sueltas > 0 && (
        <Aviso tono="atencion" resumen={`${sueltas} sin avisar`}>
          <span>
            Hay {sueltas} alarma{sueltas === 1 ? '' : 's'} que no llegó al sistema central de
            avisos, así que nadie recibió notificación de ella
            {sueltas === 1 ? '' : 's'}.{' '}
            <button
              type="button"
              className={estilos.enlaceAviso}
              onClick={() => setPropagacion('sueltas')}
            >
              Ver cuáles
            </button>
          </span>
        </Aviso>
      )}

      <BarraFiltros
        chips={chips}
        acciones={
          chips.length > 0 && (
            <Button
              variante="fantasma"
              icono="cerrar"
              onClick={() => {
                setEstado('todas');
                setPropagacion('todas');
                setBusqueda('');
              }}
            >
              Limpiar filtros
            </Button>
          )
        }
      >
        <Input
          etiqueta="Buscar"
          icono="buscar"
          placeholder="Medidor o magnitud"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
        <Select
          etiqueta="Medidor"
          placeholder="Todos los medidores"
          value={medidorFiltro}
          onChange={(e) => setMedidorFiltro(e.target.value)}
          opciones={medidores.map((m) => ({ valor: m, etiqueta: m }))}
        />
        <Select
          etiqueta="Estado"
          value={estado}
          onChange={(e) => setEstado(e.target.value as typeof estado)}
          opciones={[
            { valor: 'todas', etiqueta: 'Todas' },
            { valor: 'activa', etiqueta: 'Solo activas' },
            { valor: 'resuelta', etiqueta: 'Solo resueltas' },
          ]}
        />
        <Select
          etiqueta="Aviso enviado"
          value={propagacion}
          onChange={(e) => setPropagacion(e.target.value as FiltroPropagacion)}
          ayuda="Si llegó al sistema central de alarmas"
          opciones={[
            { valor: 'todas', etiqueta: 'Todas' },
            { valor: 'propagadas', etiqueta: 'Con aviso enviado' },
            { valor: 'sueltas', etiqueta: 'Sin aviso enviado' },
          ]}
        />
      </BarraFiltros>

      {consulta.isLoading ? (
        <ListaEsqueleto filas={6} />
      ) : consulta.isError ? (
        <EstadoError
          acciones={
            <Button variante="secundario" icono="refrescar" onClick={refrescar}>
              Reintentar
            </Button>
          }
        />
      ) : filtradas.length === 0 ? (
        <EstadoTodoBien
          titulo={alarmas.length === 0 ? 'Sin alarmas de energía' : 'Ninguna coincide'}
          descripcion={
            alarmas.length === 0
              ? 'Los medidores no han superado ningún umbral configurado.'
              : 'Prueba a cambiar la búsqueda o los filtros.'
          }
        />
      ) : (
        <Tabla
          datos={filtradas}
          columnas={columnas}
          claveFila={(a) => a.id}
          etiqueta="Alarmas de energía"
        />
      )}
    </div>
  );
}
